import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardMedia
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SaveIcon from '@mui/icons-material/Save';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { addProduct } from '../services/productService';

/**
 * Admin Add Product - Aggiungi nuovo prodotto con scanner barcode integrato
 */
function AdminAddProduct() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [product, setProduct] = useState({
    barcode: '',
    name: '',
    brand: '',
    price: '',
    originalPrice: '',
    description: '',
    category: '',
    sizes: [],
    colors: [],
    material: '',
    season: '',
    imageUrl: ''
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const html5QrCodeRef = useRef(null);

  const steps = ['Scansiona Barcode', 'Dettagli Prodotto', 'Foto Prodotto', 'Conferma'];

  const availableSizes = {
    'Donna': ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
    'Uomo': ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'],
    'Bambini': ['2Y', '4Y', '6Y', '8Y', '10Y', '12Y', '14Y'],
    'Pantaloni': ['26', '28', '30', '32', '34', '36', '38', '40']
  };

  const availableColors = [
    'Nero', 'Bianco', 'Grigio', 'Blu', 'Blu navy', 'Rosso', 'Verde',
    'Giallo', 'Arancione', 'Rosa', 'Viola', 'Marrone', 'Beige', 'Multicolore'
  ];

  const startBarcodeScanner = async () => {
    try {
      setScanning(true);
      const formatsToSupport = [
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.UPC_A,
        Html5QrcodeSupportedFormats.UPC_E,
        Html5QrcodeSupportedFormats.CODE_128
      ];

      const html5QrCode = new Html5Qrcode("barcode-reader", { formatsToSupport });
      html5QrCodeRef.current = html5QrCode;

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 150 }
      };

      await html5QrCode.start(
        { facingMode: "environment" },
        config,
        (decodedText) => {
          console.log('✅ Barcode scanned:', decodedText);
          setProduct(prev => ({ ...prev, barcode: decodedText }));
          stopBarcodeScanner();
          setActiveStep(1); // Move to next step
        },
        (error) => {
          // Ignore scan failures
        }
      );
    } catch (err) {
      console.error('Scanner error:', err);
      alert('Errore nell\'avvio dello scanner');
      setScanning(false);
    }
  };

  const stopBarcodeScanner = async () => {
    if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
    setScanning(false);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setProduct(prev => ({ ...prev, imageUrl: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSizeToggle = (size) => {
    setProduct(prev => ({
      ...prev,
      sizes: prev.sizes.includes(size)
        ? prev.sizes.filter(s => s !== size)
        : [...prev.sizes, size]
    }));
  };

  const handleColorToggle = (color) => {
    setProduct(prev => ({
      ...prev,
      colors: prev.colors.includes(color)
        ? prev.colors.filter(c => c !== color)
        : [...prev.colors, color]
    }));
  };

  const handleSaveProduct = async () => {
    setSaving(true);
    setError(null);

    try {
      console.log('💾 Saving product to Firebase:', product);

      // Validate required fields
      if (!product.barcode || !product.name || !product.price) {
        throw new Error('Barcode, nome e prezzo sono obbligatori');
      }

      // Convert price strings to numbers
      const productData = {
        ...product,
        price: parseFloat(product.price),
        originalPrice: product.originalPrice ? parseFloat(product.originalPrice) : null
      };

      // Save to Firebase
      const productId = await addProduct(productData);

      console.log(`✅ Product saved with ID: ${productId}`);
      setSuccess(true);

      // Redirect dopo 2 secondi
      setTimeout(() => {
        navigate('/admin');
      }, 2000);

    } catch (err) {
      console.error('❌ Error saving product:', err);
      setError(err.message || 'Errore nel salvataggio del prodotto');
      setSaving(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ backgroundColor: '#1a1a1a' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/admin')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Aggiungi Nuovo Prodotto
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Stepper */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Success Message */}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            ✅ Prodotto salvato con successo! Reindirizzamento...
          </Alert>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            ❌ {error}
          </Alert>
        )}

        {/* Step 0: Scan Barcode */}
        {activeStep === 0 && (
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              Scansiona il Barcode del Prodotto
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Usa lo scanner per leggere il barcode EAN/UPC del capo
            </Typography>

            {!scanning && !product.barcode && (
              <Box sx={{ textAlign: 'center', my: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<QrCodeScannerIcon />}
                  onClick={startBarcodeScanner}
                  sx={{ mb: 2 }}
                >
                  Avvia Scanner
                </Button>
                <Typography variant="body2" color="text.secondary">
                  oppure
                </Typography>
                <TextField
                  fullWidth
                  label="Inserisci Barcode Manualmente"
                  value={product.barcode}
                  onChange={(e) => setProduct(prev => ({ ...prev, barcode: e.target.value }))}
                  sx={{ mt: 2 }}
                  placeholder="es. 8434078901234"
                />
              </Box>
            )}

            {scanning && (
              <Box sx={{ my: 3 }}>
                <div id="barcode-reader" style={{ width: '100%' }} />
                <Button
                  variant="outlined"
                  onClick={stopBarcodeScanner}
                  sx={{ mt: 2 }}
                  fullWidth
                >
                  Annulla Scansione
                </Button>
              </Box>
            )}

            {product.barcode && !scanning && (
              <Box sx={{ my: 3 }}>
                <Alert severity="success">
                  Barcode rilevato: <strong>{product.barcode}</strong>
                </Alert>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  Continua
                </Button>
              </Box>
            )}
          </Paper>
        )}

        {/* Step 1: Product Details */}
        {activeStep === 1 && (
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              Dettagli Prodotto
            </Typography>

            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Barcode"
                  value={product.barcode}
                  disabled
                  variant="filled"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label="Nome Prodotto"
                  value={product.name}
                  onChange={(e) => setProduct(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="es. Maglia Elegante"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label="Brand"
                  value={product.brand}
                  onChange={(e) => setProduct(prev => ({ ...prev, brand: e.target.value }))}
                  placeholder="es. Zara"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Prezzo (€)"
                  value={product.price}
                  onChange={(e) => setProduct(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="29.99"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Prezzo Originale (€)"
                  value={product.originalPrice}
                  onChange={(e) => setProduct(prev => ({ ...prev, originalPrice: e.target.value }))}
                  placeholder="49.99"
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Descrizione"
                  value={product.description}
                  onChange={(e) => setProduct(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descrivi il prodotto..."
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Categoria</InputLabel>
                  <Select
                    value={product.category}
                    label="Categoria"
                    onChange={(e) => setProduct(prev => ({ ...prev, category: e.target.value, sizes: [] }))}
                  >
                    <MenuItem value="Donna">Donna</MenuItem>
                    <MenuItem value="Uomo">Uomo</MenuItem>
                    <MenuItem value="Bambini">Bambini</MenuItem>
                    <MenuItem value="Accessori">Accessori</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Materiale"
                  value={product.material}
                  onChange={(e) => setProduct(prev => ({ ...prev, material: e.target.value }))}
                  placeholder="es. Cotone 100%"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Stagione</InputLabel>
                  <Select
                    value={product.season}
                    label="Stagione"
                    onChange={(e) => setProduct(prev => ({ ...prev, season: e.target.value }))}
                  >
                    <MenuItem value="Primavera/Estate">Primavera/Estate</MenuItem>
                    <MenuItem value="Autunno/Inverno">Autunno/Inverno</MenuItem>
                    <MenuItem value="Tutto l'anno">Tutto l'anno</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Sizes */}
              {product.category && (
                <Grid size={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Taglie Disponibili
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {availableSizes[product.category]?.map((size) => (
                      <Chip
                        key={size}
                        label={size}
                        onClick={() => handleSizeToggle(size)}
                        color={product.sizes.includes(size) ? 'primary' : 'default'}
                        variant={product.sizes.includes(size) ? 'filled' : 'outlined'}
                        sx={{
                          minHeight: { xs: '44px', sm: '32px' },
                          minWidth: { xs: '44px', sm: 'auto' },
                          fontSize: { xs: '14px', sm: '13px' },
                          cursor: 'pointer'
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Colors */}
              <Grid size={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Colori Disponibili
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {availableColors.map((color) => (
                    <Chip
                      key={color}
                      label={color}
                      onClick={() => handleColorToggle(color)}
                      color={product.colors.includes(color) ? 'primary' : 'default'}
                      variant={product.colors.includes(color) ? 'filled' : 'outlined'}
                      sx={{
                        minHeight: { xs: '44px', sm: '32px' },
                        minWidth: { xs: '60px', sm: 'auto' },
                        fontSize: { xs: '13px', sm: '13px' },
                        cursor: 'pointer'
                      }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                onClick={handleBack}
                sx={{
                  minHeight: { xs: '44px', sm: '36px' },
                  minWidth: { xs: '100px', sm: '80px' },
                  fontSize: { xs: '15px', sm: '14px' }
                }}
              >
                Indietro
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!product.name || !product.brand || !product.price || !product.category}
                sx={{
                  minHeight: { xs: '44px', sm: '36px' },
                  minWidth: { xs: '120px', sm: '100px' },
                  fontSize: { xs: '15px', sm: '14px' }
                }}
              >
                Continua
              </Button>
            </Box>
          </Paper>
        )}

        {/* Step 2: Photo */}
        {activeStep === 2 && (
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              Foto Prodotto
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Carica una foto frontale del capo su sfondo bianco per migliori risultati AI
            </Typography>

            <Box sx={{ textAlign: 'center', my: 4 }}>
              {imagePreview && (
                <Card sx={{ maxWidth: 400, mx: 'auto', mb: 3 }}>
                  <CardMedia
                    component="img"
                    image={imagePreview}
                    alt="Product preview"
                    sx={{ maxHeight: 500, objectFit: 'contain' }}
                  />
                </Card>
              )}

              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="photo-upload"
                type="file"
                onChange={handleImageUpload}
              />
              <label htmlFor="photo-upload">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<PhotoCameraIcon />}
                  size="large"
                >
                  {imagePreview ? 'Cambia Foto' : 'Carica Foto'}
                </Button>
              </label>

              <Typography variant="caption" display="block" sx={{ mt: 2 }} color="text.secondary">
                Oppure inserisci un URL immagine:
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="https://example.com/image.jpg"
                value={product.imageUrl && !imagePreview ? product.imageUrl : ''}
                onChange={(e) => setProduct(prev => ({ ...prev, imageUrl: e.target.value }))}
                sx={{ mt: 1, maxWidth: 400 }}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button onClick={handleBack}>
                Indietro
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!product.imageUrl}
              >
                Continua
              </Button>
            </Box>
          </Paper>
        )}

        {/* Step 3: Confirm */}
        {activeStep === 3 && (
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              Conferma Dettagli
            </Typography>

            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                {product.imageUrl && (
                  <Card>
                    <CardMedia
                      component="img"
                      image={product.imageUrl}
                      alt={product.name}
                      sx={{ maxHeight: 300, objectFit: 'contain' }}
                    />
                  </Card>
                )}
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" gutterBottom>{product.name}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Brand:</strong> {product.brand}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Barcode:</strong> {product.barcode}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Prezzo:</strong> €{product.price}
                  {product.originalPrice && ` (prima €${product.originalPrice})`}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Categoria:</strong> {product.category}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Taglie:</strong> {product.sizes.join(', ') || 'Nessuna'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Colori:</strong> {product.colors.join(', ') || 'Nessuno'}
                </Typography>
                {product.material && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Materiale:</strong> {product.material}
                  </Typography>
                )}
                {product.season && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Stagione:</strong> {product.season}
                  </Typography>
                )}
              </Grid>

              <Grid size={12}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Descrizione:</strong> {product.description || 'Nessuna descrizione'}
                </Typography>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button onClick={handleBack}>
                Indietro
              </Button>
              <Button
                variant="contained"
                color="success"
                size="large"
                startIcon={!saving && <SaveIcon />}
                onClick={handleSaveProduct}
                disabled={saving}
              >
                {saving ? 'Salvataggio...' : 'Salva Prodotto'}
              </Button>
            </Box>
          </Paper>
        )}
      </Container>
    </Box>
  );
}

export default AdminAddProduct;
